<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";

import ConfirmDialog from "@/components/base/ConfirmDialog.vue";
import { useSwipe } from "@/composables/useSwipe";
import { formatClock } from "@/lib/format";
import { useAlbumStore } from "@/stores/album";
import { useNotificationsStore } from "@/stores/notifications";
import type { MediaItem } from "@/types/album";

/**
 * Port du dialogue `#lightbox` de la maquette : visionneuse plein écran.
 *
 * Deux manques comblés par rapport à l'original :
 * - les vidéos se lisent réellement, avec le son (la maquette n'affichait
 *   qu'une image fixe) ;
 * - un souvenir déposé par un invité peut être supprimé, avec confirmation.
 */

const props = defineProps<{
  /** Position courante dans `items`. `null` = visionneuse fermée. */
  index: number | null;
  items: MediaItem[];
}>();

const emit = defineEmits<{ "update:index": [value: number | null] }>();

const album = useAlbumStore();
const notifications = useNotificationsStore();

const dialog = ref<HTMLDialogElement | null>(null);
const stage = ref<HTMLElement | null>(null);

const currentItem = computed<MediaItem | undefined>(() => {
  if (props.index === null) return undefined;
  return props.items[props.index];
});

const positionLabel = computed(() => {
  if (props.index === null) return "";
  return `${props.index + 1} sur ${props.items.length}`;
});

// --- Ouverture / fermeture -----------------------------------------------

const close = (): void => emit("update:index", null);

watch(
  () => props.index,
  (index) => {
    const node = dialog.value;
    if (!node) return;
    const shouldOpen = index !== null;
    if (shouldOpen && !node.open) node.showModal();
    if (!shouldOpen && node.open) node.close();
    if (shouldOpen) showControls();
  },
  { flush: "post" },
);

const onCancel = (): void => close();
const onClose = (): void => {
  if (props.index !== null) close();
};

onBeforeUnmount(() => {
  if (dialog.value?.open) dialog.value.close();
  if (hideTimer !== null) window.clearTimeout(hideTimer);
});

// --- Navigation (boutons, clavier, balayage) ------------------------------

const goTo = (target: number): void => {
  const length = props.items.length;
  if (length === 0) {
    emit("update:index", null);
    return;
  }
  emit("update:index", ((target % length) + length) % length);
};

const goPrev = (): void => {
  if (props.index === null) return;
  goTo(props.index - 1);
};

const goNext = (): void => {
  if (props.index === null) return;
  goTo(props.index + 1);
};

const onKeydown = (event: KeyboardEvent): void => {
  if (event.key === "ArrowRight") goNext();
  if (event.key === "ArrowLeft") goPrev();
};

useSwipe(stage, { onLeft: goNext, onRight: goPrev });

// --- Masquage des commandes après inactivité ------------------------------

const controlsVisible = ref(true);
let hideTimer: number | null = null;

const scheduleHide = (): void => {
  if (hideTimer !== null) window.clearTimeout(hideTimer);
  hideTimer = window.setTimeout(() => {
    // Ne jamais escamoter les commandes tant que le focus clavier est posé sur
    // l'une d'elles : l'anneau de focus disparaîtrait avec, et l'invité qui
    // navigue au clavier perdrait sa position en plein parcours de
    // suppression (WCAG 2.4.7, Focus visible).
    if (stage.value?.contains(document.activeElement)) {
      scheduleHide();
      return;
    }
    controlsVisible.value = false;
  }, 3000);
};

const showControls = (): void => {
  controlsVisible.value = true;
  scheduleHide();
};

// --- Média affiché ---------------------------------------------------------

const showVideoPlayer = computed(
  () => currentItem.value?.kind === "video" && Boolean(currentItem.value.playbackUrl),
);

const posterStyle = computed(() => {
  const item = currentItem.value;
  if (!item) return {};
  return {
    backgroundImage: `url("${item.previewUrl}")`,
    backgroundPosition: item.crop?.position ?? "center",
    backgroundSize: item.crop?.size ?? "contain",
  };
});

// --- Favori ------------------------------------------------------------

const onLikeClick = (): void => {
  const item = currentItem.value;
  if (!item) return;
  void album.toggleLike(item.id);
};

// --- Téléchargement et partage -------------------------------------------

const mediaUrl = computed(() => {
  const item = currentItem.value;
  if (!item) return "";
  if (item.kind === "video" && item.playbackUrl) return item.playbackUrl;
  return item.previewUrl;
});

/**
 * Certaines vignettes de démonstration sont des découpes d'une même planche
 * contact : leur `previewUrl` désigne la planche entière, pas la photo. On
 * n'offre donc téléchargement et partage que lorsque l'URL pointe réellement
 * un fichier autonome — ce qui est toujours le cas des dépôts d'invités.
 */
const isStandaloneFile = computed(() => {
  const item = currentItem.value;
  if (!item) return false;
  if (item.kind === "video") return Boolean(item.playbackUrl);
  return item.crop === undefined;
});

const downloadFileName = computed(() => {
  const item = currentItem.value;
  if (!item) return undefined;
  const extension = /\.([a-z0-9]+)(?:[?#]|$)/i.exec(mediaUrl.value)?.[1];
  return `souvenir-${item.id}.${extension ?? (item.kind === "video" ? "mp4" : "jpg")}`;
});

const canShare =
  typeof navigator !== "undefined" &&
  typeof navigator.share === "function" &&
  typeof navigator.canShare === "function";

const onShareClick = (): void => {
  void share();
};

const share = async (): Promise<void> => {
  const item = currentItem.value;
  if (!item) return;
  try {
    const response = await fetch(mediaUrl.value);
    const blob = await response.blob();
    const file = new File([blob], downloadFileName.value ?? `souvenir-${item.id}`, {
      type: blob.type,
    });
    if (!navigator.canShare({ files: [file] })) {
      notifications.notify(
        "Le partage de ce souvenir n’est pas possible sur cet appareil.",
        "error",
      );
      return;
    }
    await navigator.share({ files: [file], title: item.caption });
  } catch (caught) {
    if (caught instanceof DOMException && caught.name === "AbortError") return;
    notifications.notify("Le partage a échoué. Réessayez.", "error");
  }
};

// --- Suppression avec confirmation ----------------------------------------

const showDeleteConfirm = ref(false);

const deleteMessage = computed(() =>
  currentItem.value?.kind === "video"
    ? "Cette vidéo sera définitivement retirée de cet appareil."
    : "Cette photo sera définitivement retirée de cet appareil.",
);

const requestDelete = (): void => {
  if (!currentItem.value) return;
  showDeleteConfirm.value = true;
};

const onDeleteDialogOpenChange = (open: boolean): void => {
  showDeleteConfirm.value = open;
};

const confirmDelete = async (): Promise<void> => {
  const item = currentItem.value;
  const index = props.index;
  if (!item || index === null) return;

  await album.removeMedia(item.id);
  notifications.notify(
    item.kind === "video"
      ? "La vidéo a été supprimée de cet appareil."
      : "La photo a été supprimée de cet appareil.",
    "success",
  );

  // `props.items` reflète déjà le tableau raccourci : on reste sur
  // l'élément qui prend la place du souvenir supprimé, on recule d'un cran
  // si c'était le dernier, ou on ferme s'il ne reste plus rien.
  const newLength = props.items.length;
  if (newLength === 0) {
    emit("update:index", null);
  } else if (index < newLength) {
    emit("update:index", index);
  } else {
    emit("update:index", newLength - 1);
  }
};

const onConfirmDelete = (): void => {
  void confirmDelete();
};
</script>

<template>
  <dialog
    ref="dialog"
    class="lightbox"
    data-testid="lightbox"
    aria-label="Visionneuse de souvenirs"
    @cancel="onCancel"
    @close="onClose"
    @keydown="onKeydown"
  >
    <div
      v-if="currentItem"
      ref="stage"
      class="lightbox__stage"
      @pointermove="showControls"
      @touchstart="showControls"
      @focusin="showControls"
      @keydown="showControls"
    >
      <button
        type="button"
        class="lightbox__close"
        data-testid="lightbox-close"
        :class="{ 'is-hidden': !controlsVisible }"
        aria-label="Fermer"
        @click="close"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18"></path>
        </svg>
      </button>
      <button
        type="button"
        class="lightbox__nav lightbox__nav--prev"
        data-testid="lightbox-prev"
        :class="{ 'is-hidden': !controlsVisible }"
        aria-label="Précédent"
        @click="goPrev"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m15 5-7 7 7 7"></path>
        </svg>
      </button>

      <div :key="currentItem.id" class="lightbox__image">
        <video
          v-if="showVideoPlayer"
          class="lightbox__video"
          data-testid="lightbox-video"
          controls
          controlslist="nodownload noplaybackrate noremoteplayback"
          disablepictureinpicture
          playsinline
          preload="metadata"
          :src="currentItem.playbackUrl"
        ></video>
        <template v-else>
          <div class="lightbox__poster" data-testid="lightbox-image" :style="posterStyle"></div>
          <div v-if="currentItem.kind === 'video'" class="lightbox__demo-badge">
            <span class="lightbox__play-badge" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="m9 7 8 5-8 5V7Z"></path></svg>
            </span>
            <small>Aperçu de démonstration</small>
          </div>
        </template>
      </div>

      <button
        type="button"
        class="lightbox__nav lightbox__nav--next"
        data-testid="lightbox-next"
        :class="{ 'is-hidden': !controlsVisible }"
        aria-label="Suivant"
        @click="goNext"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m9 5 7 7-7 7"></path>
        </svg>
      </button>

      <div class="lightbox__info" :class="{ 'is-hidden': !controlsVisible }">
        <div>
          <strong data-testid="lightbox-caption">{{ currentItem.caption }}</strong>
          <span
            ><b data-testid="lightbox-author">{{ currentItem.author }}</b> ·
            <time>{{ formatClock(currentItem.createdAt) }}</time></span
          >
        </div>
        <div class="lightbox__actions">
          <a
            v-if="isStandaloneFile"
            class="lightbox__action"
            data-testid="lightbox-download"
            :href="mediaUrl"
            :download="downloadFileName"
            aria-label="Télécharger ce souvenir"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 4v11m0 0 4-4m-4 4-4-4M5 19h14"></path>
            </svg>
          </a>
          <button
            v-if="canShare && isStandaloneFile"
            type="button"
            class="lightbox__action"
            data-testid="lightbox-share"
            aria-label="Partager ce souvenir"
            @click="onShareClick"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 15V4m0 0 4 4m-4-4-4 4M5 13v6h14v-6"></path>
            </svg>
          </button>
          <button
            type="button"
            class="lightbox__action"
            data-testid="lightbox-delete"
            aria-label="Supprimer ce souvenir"
            @click="requestDelete"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M5 7h14M9 7V5h6v2m-9 0 1 13h10l1-13" stroke-linejoin="round"></path>
            </svg>
          </button>
          <button
            type="button"
            class="lightbox__like"
            data-testid="lightbox-like"
            :class="{ 'is-liked': currentItem.liked }"
            :aria-pressed="currentItem.liked"
            aria-label="Ajouter aux favoris"
            @click="onLikeClick"
          >
            {{ currentItem.liked ? "♥" : "♡" }}
          </button>
        </div>
      </div>

      <p class="u-sr-only" data-testid="lightbox-position" aria-live="polite">
        {{ positionLabel }}
      </p>
    </div>
  </dialog>

  <ConfirmDialog
    :open="showDeleteConfirm"
    title="Supprimer ce souvenir ?"
    :message="deleteMessage"
    tone="danger"
    @update:open="onDeleteDialogOpenChange"
    @confirm="onConfirmDelete"
    @cancel="showDeleteConfirm = false"
  />
</template>

<style scoped>
.lightbox {
  width: 100%;
  max-width: none;
  height: 100%;
  max-height: none;
  margin: 0;
  padding: 0;
  color: white;
  border: 0;
  background: var(--color-night);
}

.lightbox::backdrop {
  background: var(--color-night);
}

.lightbox__stage {
  position: relative;
  width: 100%;
  height: 100%;
}

.lightbox__image {
  position: absolute;
  inset: 0;
}

.lightbox__poster {
  position: absolute;
  inset: 0;
  background-repeat: no-repeat;
}

.lightbox__video {
  display: block;
  width: 100%;
  height: 100%;
  padding-bottom: 108px;
  background: black;
  object-fit: contain;
}

@media (min-width: 760px) {
  .lightbox__video {
    padding-bottom: 96px;
  }
}

.lightbox__demo-badge {
  position: absolute;
  top: 50%;
  left: 50%;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 8px;
  transform: translate(-50%, -50%);
}

.lightbox__play-badge {
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  border: 1px solid rgb(255 255 255 / 0.45);
  border-radius: 50%;
  background: rgb(25 31 27 / 0.42);
  backdrop-filter: blur(7px);
}

.lightbox__play-badge svg {
  width: 22px;
  fill: white;
}

.lightbox__demo-badge small {
  padding: 4px 9px;
  border-radius: 99px;
  background: rgb(20 25 22 / 0.57);
  font-size: 12px;
  letter-spacing: 0.04em;
}

.lightbox__close,
.lightbox__nav,
.lightbox__action,
.lightbox__like {
  transition: opacity 0.25s ease;
}

.lightbox__close.is-hidden,
.lightbox__nav.is-hidden,
.lightbox__info.is-hidden {
  opacity: 0;
  pointer-events: none;
}

.lightbox__close,
.lightbox__nav {
  position: absolute;
  z-index: 2;
  display: grid;
  place-items: center;
  padding: 0;
  color: white;
  border: 1px solid rgb(255 255 255 / 0.18);
  border-radius: 50%;
  background: rgb(19 25 21 / 0.45);
  backdrop-filter: blur(7px);
}

.lightbox__close,
.lightbox__nav {
  width: 38px;
  height: 38px;
}

.lightbox__close svg,
.lightbox__nav svg {
  width: 17px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.7;
}

.lightbox__close {
  top: max(18px, env(safe-area-inset-top));
  right: 18px;
}

.lightbox__nav {
  top: 50%;
  transform: translateY(-50%);
}

.lightbox__nav--prev {
  left: 12px;
}

.lightbox__nav--next {
  right: 12px;
}

.lightbox__info {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 50px 24px max(26px, env(safe-area-inset-bottom));
  background: linear-gradient(0deg, rgb(15 20 17 / 0.88), transparent);
}

.lightbox__info > div:first-child {
  min-width: 0;
}

.lightbox__info strong,
.lightbox__info span {
  display: block;
}

.lightbox__info strong {
  font-family: var(--font-serif);
  font-size: 18px;
  font-weight: 400;
}

.lightbox__info span {
  margin-top: 5px;
  color: rgb(255 255 255 / 0.62);
  font-size: 12px;
}

.lightbox__info b {
  font-weight: 600;
}

.lightbox__actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 14px;
}

/* La zone de frappe monte à 44 px sans grossir le pictogramme : ces commandes
   sont manipulées au pouce, en plein écran, et 22 px passaient sous le
   minimum de 24 px du critère WCAG 2.5.8 (Target Size, Minimum). */
.lightbox__action {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  margin: -11px;
  padding: 0;
  color: rgb(255 255 255 / 0.85);
  border: 0;
  background: none;
}

.lightbox__action svg {
  width: 19px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.5;
}

.lightbox__like {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  padding: 0;
  color: white;
  border: 0;
  background: none;
  font-family: var(--font-serif);
  font-size: 32px;
  line-height: 1;
}

.lightbox__like.is-liked {
  color: #e49b8e;
}
</style>
